import { notification } from 'antd'
import { useEffect, useState } from 'react'
import { useContext } from 'use-context-selector'

import { PromoPageStateContext } from 'states/PromoPageState/PromoPageStateProvider'
import { saveSection } from 'states/PromoPageState/reducer'
import { SectionState } from 'states/PromoPageState/state'
import { ConferenceType } from 'sections/PromoSection/pages/ConferencesPage/types'
import { AdminHeaderBlockElem } from 'components/AdminV2/UI/HeaderBlockElem/AdminHeaderBlockElem'
import { AdminInput } from 'components/AdminV2/UI/Input/AdminInput'
import { AdminSaveButton } from 'components/AdminV2/UI/SaveButton/AdminSaveButton'
import { AdminTextArea } from 'components/AdminV2/UI/TextArea/AdminTextArea'
import { useCalendar } from 'utils/hooks/useCalendar'
import { ALL_EXCEPT_NUMBERS_REGEXP } from 'shared/constants/regExp'
import { ConferenceData } from 'types/types'

import s from './ConferenceBlockItem.module.scss'

type ConferenceBlockItemProps = {
  index: number
  calendarState: ReturnType<typeof useCalendar>['state']
  localPromoState: SectionState
  existingConferences: ConferenceType[]
  conference: ConferenceType
  setLocalPromoState: React.Dispatch<React.SetStateAction<SectionState>>
  handleSave: (conferences: ConferenceType) => void
}

export const ConferenceBlockItem = ({
  index,
  calendarState,
  localPromoState,
  existingConferences,
  conference,
  setLocalPromoState,
  handleSave,
}: ConferenceBlockItemProps) => {
  const ctx = useContext(PromoPageStateContext)

  const [conferenceLocal, setConferenceLocal] = useState(conference)
  const [isEdit, setIsEdit] = useState(false)

  const [startTime, setStartTime] = useState<{ hours: string; minutes: string }>({
    hours: String(new Date(conference.startDate).getHours()),
    minutes: String(new Date(conference.startDate).getMinutes()).padStart(2, '0'),
  })
  const [endTime, setEndTime] = useState<{ hours: string; minutes: string }>({
    hours: String(new Date(conference.endDate).getHours()),
    minutes: String(new Date(conference.endDate).getMinutes()).padStart(2, '0'),
  })

  const disabled =
    conferenceLocal.title.trim().length === 0 ||
    conferenceLocal.description.trim().length === 0 ||
    conferenceLocal.link.trim().length === 0

  useEffect(() => {
    if (conference.title) {
      setIsEdit(true)
    }
  }, [])

  const deleteConferenceHandler = () => {
    const stateCopy = { ...localPromoState }
    const conferences = (stateCopy.content as ConferenceData).conferenceData[calendarState.selectedYear][
      calendarState.selectedMonth.monthIndex
    ][calendarState.selectedDate.dayNumber]

    const filteredConferences = conferences.filter((conference) => conference.index !== index)

    for (let i = 0; i < filteredConferences.length; i++) {
      filteredConferences[i].index = i
    }

    ;(stateCopy.content as ConferenceData).conferenceData[calendarState.selectedYear][
      calendarState.selectedMonth.monthIndex
    ][calendarState.selectedDate.dayNumber] = filteredConferences

    setLocalPromoState(stateCopy)

    ctx.dispatch(saveSection(stateCopy))
  }

  const timeHandler = (value: string, type: 'hours' | 'minutes', startOrEnd: 'start' | 'end') => {
    const numberValue = value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')

    if (+numberValue > 59) {
      return
    }

    const hours = numberValue
    let minutes = numberValue
    const currentTime = startOrEnd === 'start' ? startTime : endTime

    if (numberValue.length === 1 && numberValue !== '0') {
      minutes = currentTime.minutes[0] === '0' || currentTime.minutes.length === 0 ? `0${numberValue}` : numberValue
    } else if (numberValue.length === 3 && numberValue[0] === '0') {
      minutes = `${numberValue[1]}${numberValue[2]}`
    }

    if (type === 'hours') {
      startOrEnd === 'start'
        ? setStartTime({ hours, minutes: startTime.minutes })
        : setEndTime({ hours, minutes: endTime.minutes })
    } else {
      startOrEnd === 'start'
        ? setStartTime({ hours: startTime.hours, minutes })
        : setEndTime({ hours: endTime.hours, minutes })
    }
  }

  const changeInputHandler = (value: string, key: keyof ConferenceType) => {
    setConferenceLocal({ ...conferenceLocal, [key]: value })
  }

  const handleConferenceSave = () => {
    const startDate = new Date(new Date(conferenceLocal.startDate).setHours(+startTime.hours, +startTime.minutes))
    const endDate = new Date(new Date(conferenceLocal.endDate).setHours(+endTime.hours, +endTime.minutes))

    const sameTime = existingConferences.some(
      (conference) => new Date(conference.startDate).getTime() === startDate.getTime(),
    )

    if (startDate.getTime() > endDate.getTime() || startDate.getTime() === endDate.getTime()) {
      notification.error({
        message: 'Ошибка',
        description: 'Время окончания не может быть раньше или равно времени начала',
      })
      return
    }

    if (sameTime && isEdit === false) {
      notification.error({
        message: 'Ошибка',
        description: 'Конференция с таким временем начала уже существует',
      })
      return
    }
    notification.success({
      message: 'Успешно',
      description: 'Изменения сохранены!',
    })
    handleSave({ ...conferenceLocal, startDate, endDate })
  }

  return (
    <div className={s.wrap}>
      <AdminHeaderBlockElem index={index} text={'Конференция'} onClick={deleteConferenceHandler} />

      <AdminInput
        label="Заголовок"
        value={conferenceLocal.title}
        onChange={(value: string) => changeInputHandler(value, 'title')}
        labelSize="16px"
        maxLength={100}
      />
      <AdminTextArea
        label="Описание"
        value={conferenceLocal.description}
        onChange={(value: string) => changeInputHandler(value, 'description')}
        labelSize="16px"
        maxLength={300}
      />
      <AdminInput
        label="Ссылка на конференцию"
        value={conferenceLocal.link}
        onChange={(value: string) => changeInputHandler(value, 'link')}
        labelSize="16px"
      />
      {isEdit && (
        <AdminInput
          label="Ссылка на запись конференции (необязательно)"
          value={conferenceLocal.linkToVideo}
          onChange={(value: string) => changeInputHandler(value, 'linkToVideo')}
          labelSize="16px"
        />
      )}

      <p>Время начала</p>
      <div className={s.timeWrapper}>
        <AdminInput
          label="Часы"
          value={startTime.hours}
          onChange={(value: string) => timeHandler(value, 'hours', 'start')}
          labelSize="16px"
          maxLength={2}
        />
        <AdminInput
          label="Минуты"
          value={startTime.minutes}
          onChange={(value: string) => timeHandler(value, 'minutes', 'start')}
          labelSize="16px"
          maxLength={3}
        />
      </div>

      <p>Время окончания</p>
      <div className={s.timeWrapper}>
        <AdminInput
          label="Часы"
          value={endTime.hours}
          onChange={(value: string) => timeHandler(value, 'hours', 'end')}
          labelSize="16px"
          maxLength={2}
        />
        <AdminInput
          label="Минуты"
          value={endTime.minutes}
          onChange={(value: string) => timeHandler(value, 'minutes', 'end')}
          labelSize="16px"
          maxLength={3}
        />
      </div>

      <AdminSaveButton
        onClick={handleConferenceSave}
        text="Сохранить конференцию"
        disabled={disabled}
        showSuccess={false}
      />
    </div>
  )
}
